<template>
  <v-dialog v-model="dialog" max-width="900px" persistent>
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        color="primary"
        outlined
        block
        v-bind="attrs"
        v-on="on"
        :disabled="!selectedTrip || isDepartureTimePassed"
      >
        <v-icon left>mdi-swap-horizontal</v-icon>
        <span>Transfer Bookings</span>
        <span v-if="isDepartureTimePassed">(Departure Time Passed)</span>
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        <span class="text-h5">Transfer Bookings</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Step 1: Show route breakdown and select bookings -->
        <div v-if="step === 1">
          <div class="mb-4">
            <div class="route-summary">
              <h3 class="step-title">
                Bookings for Trip #{{ selectedTrip.id }}
              </h3>
              <p class="text-subtitle-2 mb-1">
                {{ toFromString(selectedTrip) }} -
                {{ formatDate(selectedTrip.fromTime) }}
              </p>
              <div class="text-caption">
                <v-icon small>mdi-ferry</v-icon>
                {{ selectedTrip.boat.name }}
                <span v-if="selectedTrip.boat.provider">
                  ({{ selectedTrip.boat.provider.name }})
                </span>
              </div>
            </div>
          </div>

          <div
            v-if="
              !loadingBookings &&
              routeCounts &&
              Object.keys(routeCounts).length > 0
            "
            class="mb-4"
          >
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="filterFromLocation"
                  :items="availableFromLocations"
                  label="Filter by From Location"
                  outlined
                  dense
                  clearable
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="filterToLocation"
                  :items="availableToLocations"
                  label="Filter by To Location"
                  outlined
                  dense
                  clearable
                  hide-details
                ></v-select>
              </v-col>
            </v-row>
          </div>

          <v-progress-linear
            v-if="loadingBookings"
            indeterminate
          ></v-progress-linear>

          <div
            v-if="
              !loadingBookings &&
              (!routeCounts || Object.keys(routeCounts).length === 0)
            "
          >
            <v-alert type="info">
              No bookings found for this trip that can be transferred.
            </v-alert>
          </div>

          <div
            v-if="
              !loadingBookings &&
              routeCounts &&
              Object.keys(routeCounts).length > 0
            "
          >
            <div
              v-if="
                filteredRouteCounts &&
                Object.keys(filteredRouteCounts).length === 0
              "
              class="mb-4"
            >
              <v-alert type="info">
                No routes match the selected filters.
              </v-alert>
            </div>
            <div
              v-for="(routeData, routeName) in filteredRouteCounts"
              :key="routeName"
              class="mb-4"
            >
              <v-expansion-panels v-model="expandedRoutes" multiple>
                <v-expansion-panel :disabled="routeData.count === 0">
                  <v-expansion-panel-header
                    :class="{ 'route-disabled': routeData.count === 0 }"
                  >
                    <div class="d-flex align-center w-100">
                      <v-checkbox
                        v-model="selectedRoutes"
                        :value="routeName"
                        :disabled="routeData.count === 0"
                        class="mr-3"
                        hide-details
                        @click.stop
                        @change="toggleRouteSelection(routeName)"
                      ></v-checkbox>
                      <div class="flex-grow-1">
                        <div
                          class="font-weight-medium"
                          :class="{ 'text--disabled': routeData.count === 0 }"
                        >
                          {{ routeName }}
                        </div>
                        <div
                          class="text-caption"
                          :class="{ 'text--disabled': routeData.count === 0 }"
                        >
                          {{ routeData.count }} seats
                          <span v-if="routeData.count === 0">
                            - No bookings available</span
                          >
                        </div>
                      </div>
                      <v-btn
                        v-if="routeData.count > 0"
                        small
                        text
                        color="primary"
                        @click.stop="selectAllInRoute(routeName)"
                      >
                        Select All
                      </v-btn>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content
                    v-if="routeData.count > 0 && routeData.bookings"
                  >
                    <div class="pt-2">
                      <div
                        v-for="booking in routeData.bookings"
                        :key="booking.id"
                        class="d-flex align-center mb-2 pl-4"
                      >
                        <v-checkbox
                          v-model="selectedBookings"
                          :value="booking.id"
                          class="mr-2"
                          hide-details
                        ></v-checkbox>
                        <div class="flex-grow-1">
                          <div class="font-weight-medium">
                            {{ booking.refNo }}
                          </div>
                          <div class="text-caption">
                            {{ occupiesSeatCount(booking.customers) }} seats -
                            {{ booking.email || booking.phone }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <div class="mt-4 d-flex justify-end">
              <v-btn @click="closeDialog" class="mr-2">Cancel</v-btn>
              <v-btn
                color="primary"
                @click="proceedToSelectTrip"
                :disabled="!selectedBookings || selectedBookings.length === 0"
              >
                Next: Select Target Trip ({{
                  selectedBookings ? selectedBookings.length : 0
                }}
                bookings selected)
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Step 2: Select target boat -->
        <div v-if="step === 2">
          <div class="mb-4">
            <v-btn @click="step = 1" text>
              <v-icon left>mdi-arrow-left</v-icon>
              Back to Booking Selection
            </v-btn>
          </div>

          <div class="mb-4">
            <div class="route-summary">
              <h3 class="step-title">Select Target Boat</h3>
              <p class="text-subtitle-2 mb-1">
                Transferring
                {{ selectedBookings ? selectedBookings.length : 0 }} bookings
                ({{ totalSeatsSelected }} seats)
              </p>
              <div class="text-caption">Route: {{ getSelectedRoute() }}</div>
              <div class="text-caption mt-1">
                <strong>Note:</strong> A new trip will be created with the same
                time and route using the selected boat if it does not exist in a
                similar time frame.
              </div>
            </div>
          </div>

          <v-progress-linear
            v-if="loadingBoats"
            indeterminate
          ></v-progress-linear>

          <div
            v-if="
              !loadingBoats && (!availableBoats || availableBoats.length === 0)
            "
          >
            <v-alert type="info">
              No available boats found from the same provider with sufficient
              capacity.
            </v-alert>
          </div>

          <div
            v-if="!loadingBoats && availableBoats && availableBoats.length > 0"
          >
            <v-list>
              <v-list-item
                v-for="boat in availableBoats"
                :key="boat.id"
                @click="selectTargetBoat(boat)"
                :class="{
                  'selected-boat':
                    selectedTargetBoat && selectedTargetBoat.id === boat.id,
                }"
              >
                <v-list-item-avatar>
                  <v-icon>mdi-ferry</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ boat.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    Capacity: {{ boat.capacity }} seats
                  </v-list-item-subtitle>
                  <v-list-item-subtitle>
                    Provider: {{ boat.provider.name }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-radio-group
                    v-model="selectedTargetBoatId"
                    :value="boat.id"
                  >
                    <v-radio :value="boat.id"></v-radio>
                  </v-radio-group>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <div class="mt-4 d-flex justify-end">
              <v-btn @click="step = 1" class="mr-2">Back</v-btn>
              <v-btn @click="closeDialog" class="mr-2">Cancel</v-btn>
              <v-btn
                color="primary"
                @click="confirmTransfer"
                :disabled="!selectedTargetBoat"
                :loading="transferring"
              >
                Create Trip & Transfer Bookings
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { toFromString } from "../helpers";

export default {
  name: "TransferBookings",
  props: {
    selectedTrip: Object,
    message: Function,
    errorMessage: Function,
    getTrips: Function,
  },
  data() {
    return {
      dialog: false,
      step: 1,
      loadingBookings: false,
      loadingBoats: false,
      transferring: false,
      routeCounts: null,
      allBookings: [],
      selectedRoutes: [],
      selectedBookings: [],
      expandedRoutes: [],
      availableBoats: [],
      selectedTargetBoat: null,
      selectedTargetBoatId: null,
      filterFromLocation: null,
      filterToLocation: null,
    };
  },
  computed: {
    isDepartureTimePassed() {
      if (!this.selectedTrip || !this.selectedTrip.fromTime) {
        return false;
      }
      return moment().isAfter(moment(this.selectedTrip.fromTime));
    },
    totalSeatsSelected() {
      return this.selectedBookings.reduce((total, bookingId) => {
        const booking = this.allBookings.find((b) => b.id === bookingId);
        return (
          total + (booking ? this.occupiesSeatCount(booking.customers) : 0)
        );
      }, 0);
    },
    availableFromLocations() {
      if (!this.routeCounts) return [];
      const locations = new Set();
      Object.keys(this.routeCounts).forEach((routeName) => {
        const parts = routeName.split("->");
        if (parts.length === 2) {
          const from = parts[0].trim();
          const to = parts[1].trim();
          if (!this.filterToLocation || to === this.filterToLocation) {
            locations.add(from);
          }
        }
      });
      return Array.from(locations).sort();
    },
    availableToLocations() {
      if (!this.routeCounts) return [];
      const locations = new Set();
      Object.keys(this.routeCounts).forEach((routeName) => {
        const parts = routeName.split("->");
        if (parts.length === 2) {
          const from = parts[0].trim();
          const to = parts[1].trim();
          if (!this.filterFromLocation || from === this.filterFromLocation) {
            locations.add(to);
          }
        }
      });
      return Array.from(locations).sort();
    },
    filteredRouteCounts() {
      if (!this.routeCounts) return null;

      if (!this.filterFromLocation && !this.filterToLocation) {
        return this.routeCounts;
      }

      const filtered = {};
      Object.entries(this.routeCounts).forEach(([routeName, routeData]) => {
        const parts = routeName.split("->");
        if (parts.length !== 2) {
          filtered[routeName] = routeData;
          return;
        }

        const from = parts[0].trim();
        const to = parts[1].trim();
        const matchesFrom =
          !this.filterFromLocation || from === this.filterFromLocation;
        const matchesTo =
          !this.filterToLocation || to === this.filterToLocation;

        if (matchesFrom && matchesTo) {
          filtered[routeName] = routeData;
        }
      });

      return filtered;
    },
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        this.loadBookingsForTransfer();
      } else {
        this.resetState();
      }
    },
    selectedBookings() {
      this.updateRouteSelections();
    },
    selectedTargetBoatId(newVal) {
      this.selectedTargetBoat = this.availableBoats.find(
        (boat) => boat.id === newVal
      );
    },
    filterFromLocation(newVal) {
      if (this.filterToLocation && newVal) {
        const isValidCombination = Object.keys(this.routeCounts || {}).some(
          (routeName) => {
            const parts = routeName.split("->");
            if (parts.length === 2) {
              const from = parts[0].trim();
              const to = parts[1].trim();
              return from === newVal && to === this.filterToLocation;
            }
            return false;
          }
        );
        if (!isValidCombination) {
          this.filterToLocation = null;
        }
      }
    },
    filterToLocation(newVal) {
      if (this.filterFromLocation && newVal) {
        const isValidCombination = Object.keys(this.routeCounts || {}).some(
          (routeName) => {
            const parts = routeName.split("->");
            if (parts.length === 2) {
              const from = parts[0].trim();
              const to = parts[1].trim();
              return from === this.filterFromLocation && to === newVal;
            }
            return false;
          }
        );
        if (!isValidCombination) {
          this.filterFromLocation = null;
        }
      }
    },
  },
  methods: {
    resetState() {
      this.step = 1;
      this.routeCounts = null;
      this.allBookings = [];
      this.selectedRoutes = [];
      this.selectedBookings = [];
      this.expandedRoutes = [];
      this.availableBoats = [];
      this.selectedTargetBoat = null;
      this.selectedTargetBoatId = null;
      this.filterFromLocation = null;
      this.filterToLocation = null;
    },

    toggleRouteSelection(routeName) {
      if (this.selectedRoutes.includes(routeName)) {
        this.selectAllInRoute(routeName);
      } else {
        this.deselectAllInRoute(routeName);
      }
    },

    selectAllInRoute(routeName) {
      if (
        !this.routeCounts ||
        !this.routeCounts[routeName] ||
        !this.routeCounts[routeName].bookings
      )
        return;

      const routeBookingIds = this.routeCounts[routeName].bookings.map(
        (b) => b.id
      );
      for (const bookingId of routeBookingIds) {
        if (!this.selectedBookings.includes(bookingId)) {
          this.selectedBookings.push(bookingId);
        }
      }

      if (!this.selectedRoutes.includes(routeName)) {
        this.selectedRoutes.push(routeName);
      }
    },

    deselectAllInRoute(routeName) {
      if (
        !this.routeCounts ||
        !this.routeCounts[routeName] ||
        !this.routeCounts[routeName].bookings
      )
        return;

      const routeBookingIds = this.routeCounts[routeName].bookings.map(
        (b) => b.id
      );
      this.selectedBookings = this.selectedBookings.filter(
        (bookingId) => !routeBookingIds.includes(bookingId)
      );

      this.selectedRoutes = this.selectedRoutes.filter((r) => r !== routeName);
    },

    updateRouteSelections() {
      if (!this.routeCounts) return;

      const newSelectedRoutes = [];

      for (const [routeName, routeData] of Object.entries(this.routeCounts)) {
        if (routeData && routeData.bookings && routeData.bookings.length > 0) {
          const routeBookingIds = routeData.bookings.map((b) => b.id);
          const allSelected = routeBookingIds.every((id) =>
            this.selectedBookings.includes(id)
          );

          if (allSelected) {
            newSelectedRoutes.push(routeName);
          }
        }
      }

      this.selectedRoutes = newSelectedRoutes;
    },

    closeDialog() {
      this.dialog = false;
    },

    sortRoutesByBookingCount() {
      if (!this.routeCounts || Object.keys(this.routeCounts).length === 0)
        return;

      const routeEntries = Object.entries(this.routeCounts);
      routeEntries.sort((a, b) => {
        const countA = a[1].count || 0;
        const countB = b[1].count || 0;
        return countB - countA;
      });

      const sortedRouteCounts = {};
      for (const [routeName, routeData] of routeEntries) {
        sortedRouteCounts[routeName] = routeData;
      }

      this.routeCounts = sortedRouteCounts;
    },

    loadBookingsForTransfer() {
      if (!this.selectedTrip) return;

      this.loadingBookings = true;
      axios({
        url: `${
          process.env.VUE_APP_BASE_URL ?? ""
        }/api/getbookingsfortransfer?tripId=${this.selectedTrip.id}`,
        method: "GET",
        headers: { Authorization: localStorage.getItem("Authorization") },
      })
        .then((resp) => {
          this.routeCounts = resp.data.routeCounts;
          this.allBookings = resp.data.bookings;
          this.sortRoutesByBookingCount();
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          }
        })
        .finally(() => {
          this.loadingBookings = false;
        });
    },

    proceedToSelectTrip() {
      this.step = 2;
      this.loadAvailableBoats();
    },

    loadAvailableBoats() {
      if (!this.selectedTrip || this.selectedBookings.length === 0) return;

      this.loadingBoats = true;
      const params = new URLSearchParams({
        originalTripId: this.selectedTrip.id.toString(),
        requiredSeats: this.totalSeatsSelected.toString(),
      });

      axios({
        url: `${
          process.env.VUE_APP_BASE_URL ?? ""
        }/api/getavailableboatsfortransfer?${params}`,
        method: "GET",
        headers: { Authorization: localStorage.getItem("Authorization") },
      })
        .then((resp) => {
          this.availableBoats = resp.data.boats;
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          }
        })
        .finally(() => {
          this.loadingBoats = false;
        });
    },

    selectTargetBoat(boat) {
      this.selectedTargetBoatId = boat.id;
    },

    confirmTransfer() {
      if (!this.selectedTargetBoat || this.selectedBookings.length === 0)
        return;

      this.transferring = true;
      const data = {
        bookingIds: this.selectedBookings,
        targetBoatId: this.selectedTargetBoat.id,
        originalTripId: this.selectedTrip.id,
      };

      axios({
        url: `${process.env.VUE_APP_BASE_URL ?? ""}/api/transferbookingstoboat`,
        method: "POST",
        headers: { Authorization: localStorage.getItem("Authorization") },
        data: JSON.stringify(data),
      })
        .then((resp) => {
          this.message(resp.data.message);
          this.closeDialog();
          if (this.getTrips) {
            this.getTrips();
          }
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          }
        })
        .finally(() => {
          this.transferring = false;
        });
    },

    occupiesSeatCount(customers) {
      return customers.reduce((count, customer) => {
        return count + (customer.ageGroup.occupiesSeat ? 1 : 0);
      }, 0);
    },

    toFromString(trip) {
      return toFromString(trip, this.$store.getters.getConfigs);
    },

    formatDate(dateTime) {
      return moment(dateTime).format("dddd, MMMM Do YYYY");
    },

    formatTime(dateTime) {
      return moment(dateTime).format("h:mm A");
    },

    getSelectedRoute() {
      if (this.selectedBookings.length === 0) return "";

      const firstSelectedBooking = this.allBookings.find((b) =>
        this.selectedBookings.includes(b.id)
      );
      if (!firstSelectedBooking) return "";

      if (firstSelectedBooking.routeToStop) {
        return `${firstSelectedBooking.routeToStop.routeStop.location.name} → ${firstSelectedBooking.routeToStop.location.name}`;
      } else if (
        this.selectedTrip.fromLocation &&
        this.selectedTrip.toLocation
      ) {
        return `${this.selectedTrip.fromLocation.name} → ${this.selectedTrip.toLocation.name}`;
      }

      return "Direct Trip";
    },
  },
};
</script>

<style scoped>
.selected-trip {
  background-color: #e3f2fd;
}

.selected-boat {
  background-color: #e3f2fd;
}

.v-list-item:hover {
  background-color: #f5f5f5;
}

.v-card {
  transition: box-shadow 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

.step-title {
  color: #1976d2;
  font-weight: 500;
}

.route-summary {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 16px;
}

.v-expansion-panel-header {
  padding: 12px 16px;
}

.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
  padding: 0 16px 16px 16px;
}

.text--disabled {
  color: rgba(0, 0, 0, 0.38) !important;
}

.route-disabled {
  opacity: 0.6;
}
</style>
